import React, { memo, useMemo } from "react";
import Box from "@mui/material/Box";
import isEqual from "lodash/isEqual";

import { MIN_HEIGHT } from "../TableHeader";
import { inFillDownAreaRow } from "../../../components/TablePro/extensions/useFillDownCells";
import TableCell, { isEqualCell, TableCellStub } from "./TableCell";

export const isEqualRow = (prevProps, nextProps) => {
  const prev = prevProps;
  const next = nextProps;
  const rowIndex = nextProps.index;

  if (
    prev.style?.transform !== next.style?.transform ||
    prev.style?.height !== next.style?.height
  ) {
    return false;
  }

  // Table fields
  if (prev.fullScreen !== next.fullScreen) {
    return false;
  }

  // Table fields
  if (prev.fields !== next.fields) {
    return false;
  }

  // Entity attributes

  if (
    !isEqual(
      Object.values(prev.entities[rowIndex] || {}).sort(),
      Object.values(next.entities[rowIndex] || {}).sort()
    )
  ) {
    return false;
  }

  // useEditableCells

  if (
    (rowIndex === prev.editCoords[0]) !== (rowIndex === next.editCoords[0]) ||
    rowIndex === next.editCoords[0]
  ) {
    return false;
  }

  if (
    (rowIndex === prev.rootCoords[0]) !== (rowIndex === next.rootCoords[0]) ||
    rowIndex === next.rootCoords[0]
  ) {
    return false;
  }

  if (
    inFillDownAreaRow(rowIndex, prev.rootCoords, prev.endCoords) !==
      inFillDownAreaRow(rowIndex, next.rootCoords, next.endCoords) ||
    inFillDownAreaRow(rowIndex, next.rootCoords, next.endCoords)
  ) {
    return false;
  }

  if (
    inFillDownAreaRow(rowIndex, prev.copyDiapason[0], prev.copyDiapason[1]) !==
    inFillDownAreaRow(rowIndex, next.copyDiapason[0], next.copyDiapason[1])
  ) {
    return false;
  }

  if (prev.fillDownMode !== next.fillDownMode) {
    return false;
  }

  if (prev.fieldsProps !== next.fieldsProps) {
    return false;
  }

  return true;
};

const TableCellMemo = memo(TableCell, isEqualCell);

const TableRow = ({
  size,
  style,
  rowIndex,
  entities,
  fields,
  fieldsProps = {},
  onChange,
  fieldsWidth,

  fillDownElementProps,
  fillDownCellProps,
  fillDownMode,

  nextRow,
  prevRow,
  nextCell,
  prevCell,

  editCoords,
  rootCoords,
  endCoords,
  setEditCoords,
  setEndCoords,
  setSelectedCoords,
  copyDiapason,
  getSelectedEntities,
  getRowBackgroundColor,
}) => {
  const entity = entities[rowIndex];
  const backgroundColor = getRowBackgroundColor ? getRowBackgroundColor(entity) : undefined;

  const containerStyle = useMemo(() => {
    const translateMatch = style.transform?.match(/translateY\((.+?)px\)/);
    const translateY = translateMatch ? parseFloat(translateMatch[1]) : 0;
    return {
      ...style,
      width: fieldsWidth,
      transform: `translateY(${translateY + MIN_HEIGHT}px)`,
      ...(backgroundColor && { backgroundColor }),
    };
  }, [style, fieldsWidth, backgroundColor]);

  const isSelectedRow = rootCoords[0] === rowIndex;

  const sx = useMemo(
    () => ({
      display: "flex",
      backgroundColor: rowIndex % 2 === 0 ? "grey.50" : "common.white",
      ...(isSelectedRow && { zIndex: 1 }),
    }),
    [rowIndex, isSelectedRow]
  );

  return (
    <Box sx={sx} style={containerStyle}>
      {fields.map((field, columnIndex) => {
        const showField = field.visible && !field.inactive;
        const loading = !entity;
        const cellKey = entity?.id
          ? `entityCell--${field.id}-${entity.id}-${columnIndex}`
          : `entityCell--${field.id}-${rowIndex}-${columnIndex}`;
        const renderField = { ...field, ...fieldsProps[field.id] };

        if (!showField) return null;

        if (loading) {
          return <TableCellStub key={cellKey} field={field} fields={fields} />;
        }

        return (
          <TableCellMemo
            key={cellKey}
            size={size}
            rowIndex={rowIndex}
            columnIndex={columnIndex}
            entity={entity}
            fields={fields}
            field={renderField}
            onChange={onChange}
            fillDownElementProps={fillDownElementProps}
            fillDownCellProps={fillDownCellProps}
            editCoords={editCoords}
            rootCoords={rootCoords}
            endCoords={endCoords}
            setEditCoords={setEditCoords}
            setEndCoords={setEndCoords}
            setSelectedCoords={setSelectedCoords}
            fillDownMode={fillDownMode}
            copyDiapason={copyDiapason}
            nextRow={nextRow}
            prevRow={prevRow}
            nextCell={nextCell}
            prevCell={prevCell}
            getSelectedEntities={getSelectedEntities}
          />
        );
      })}
    </Box>
  );
};

export default TableRow;
